import { toBase64, toUrlDisplayNamePath } from './utils.url';

export const VIN_LENGTH = 17;
export const VIN_ERRORS = {
  VIN_LENGTH_ERROR: 'A VIN must contain exactly 17 characters.',
  VIN_NOT_FOUND_ERROR: "We couldn't find that VIN. Please re-enter it.",
  VIN_NOT_FOUND_ERROR_2_OPTIONS: "We couldn't find that VIN. Please re-enter it or enter your vehicle's information below.",
  VIN_ACTIVE_AUTOTRADER_LISTING_ERROR: "Your vehicle doesn't qualify for an Instant Cash Offer because it is listed for sale by a dealer.",
};

export const YMM_ERRORS = {
  YMM_MISSING: 'Based on information provided, this vehicle is ineligible for an Offer.',
};

export const MAX_PLATE_LENGTH = 10;
export const MIN_YEAR_ELIGIBLE = new Date().getFullYear() - 24;
export const isYearEligible = (year: number) => {
  return year >= MIN_YEAR_ELIGIBLE;
};

export const LICENSE_PLATE_ERRORS = {
  PLATE_NOT_FOUND_ASSIGNED: "We couldn't find that License Plate and State. Please re-enter it.",
  PLATE_NOT_FOUND_UNASSIGNED: "We couldn't find that License Plate and State. Please re-enter it or enter your vehicle's information below.",
  PLATE_MISSING: 'Enter your License Plate and State.',
};

export const MSM_MILEAGE_ERRORS = {
  MSM_MILEAGE_MISSING: 'Please enter your mileage.',
  MSM_INVALID_MILEAGE: "Vehicles with more than 300,000 miles aren't eligible for an Offer.",
};

export const MAX_MILEAGE_INPUT_LENGTH: number = 7;

export const MSM_STYLE_ERRORS = {
  MSM_STYLE_MISSING: 'Please select a style.',
};

export const MISMATCH_ERROR = "VIN does not match consumer's vehicle. Please double check the VIN or start a new offer.";
export const MISMATCH_VIN_LP_ERROR = 'VIN could not be decoded.';

// eslint-disable-next-line @stencil-community/ban-exported-const-enums
export enum DynamicEntryStep {
  FormSelector = 1,
  MSM = 2,
  NotQualify = 3,
  ThankYou = 4,
}

export const DYNAMIC_ENTRY_TABS = {
  VIN: 'vin',
  PLATE: 'plate',
  YMM: 'ymm',
};

// eslint-disable-next-line @stencil-community/ban-exported-const-enums
export enum DynamicEntryInstallationMode {
  Redirect = 'redirect',
  Offsite = 'offsite',
  PopUp = 'popup',
  Integrated = 'integrated',
}

export const VEHICLE_INFO_REQUIRED_BY_ANSWER_STATE = ['vin', 'yearId', 'make', 'makeId', 'model', 'modelId', 'trim', 'trimId', 'mileage'];

export interface YMMSuggestion {
  makeId: number;
  makeName: string;
  modelId: number;
  modelName: string;
  trimId: number;
  trimName: string;
  vehicleName: string;
  vehicleId: number;
  displayName: string;
  yearId: number;
}

interface FunnelURLParams {
  offerCode: string;
  dealerId: string;
  vin?: string;
  yearId: number;
  make: string;
  model: string;
  vehicleId: number;
  mileage?: number;
  transmissionId: number;
  engineId: number;
  drivetrainId: number;
  genericOptions: string[];
}

export const createFunnelUrl = ({
  offerCode,
  dealerId,
  vin,
  yearId,
  make,
  model,
  vehicleId,
  mileage,
  transmissionId,
  engineId,
  drivetrainId,
  genericOptions,
}: FunnelURLParams): string => {
  const urlParams = new URLSearchParams();
  offerCode && urlParams.set('offerCode', offerCode);
  dealerId && urlParams.set('dealerId', dealerId);
  const paramString = offerCode || dealerId ? `?${urlParams.toString()}` : '';

  const makeFormatted = toUrlDisplayNamePath(make);
  const modelFormatted = toUrlDisplayNamePath(model);
  const json = {
    vin,
    p: 1,
    lp: 1,
    displayYMM: true,
    pa: [offerCode, dealerId],
    m: mileage,
    s: vehicleId,
    e: engineId,
    d: drivetrainId,
    t: transmissionId,
    go: genericOptions,
  };
  const jsonStr = JSON.stringify(json);
  const hash = `#_____${toBase64(jsonStr)}`;
  const url = `${process.env.FURY_URL}/instant-cash-offer/${yearId}/${makeFormatted}/${modelFormatted}/${paramString}${hash}`;
  return url;
};

export function mapSuggestionsToValue(suggestions: YMMSuggestion[]) {
  return suggestions.map((s) => ({
    value: s.vehicleId,
    label: formatSuggestion(s),
  }));
}

export function formatSuggestion(suggestion) {
  return suggestion.trimName === suggestion.vehicleName
    ? `${suggestion.yearId} ${suggestion.makeName} ${suggestion.modelName} ${suggestion.trimName}`
    : ` ${suggestion.displayName}`;
}

export const shouldHasSecondaryYmmOption = ({ enableMakeModel = true, selectedTab = 'vin', errorMessage = '' }) => {
  const shouldHasSecondaryYmmOption =
    !enableMakeModel &&
    ((selectedTab === 'vin' && errorMessage === VIN_ERRORS.VIN_NOT_FOUND_ERROR_2_OPTIONS) ||
      (selectedTab === 'plate' && errorMessage === LICENSE_PLATE_ERRORS.PLATE_NOT_FOUND_UNASSIGNED));
  return shouldHasSecondaryYmmOption;
};

export const generateYMMString = ({ yearId, make, model, trim }) => {
  return `${yearId > 0 ? `${yearId} ` : ''}${make} ${model} ${trim}`.trim();
};

export const formatMileage = (mileageText: string) => {
  const mileage = Number(mileageText);
  return mileage ? mileage.toLocaleString() : mileageText;
};

export const generateSeeValueLink = ({ yearId, make, model, trim, trimId }) => {
  const link = `https://www.kbb.com/vehicles/hub/pricingredirect/?vehicleId=${trimId}&intent=trade-in-sell&pricetype=trade-in&year=${yearId}&make=${make}&model=${model}&style=${trim.replaceAll('/', '-')}`;
  return link;
};
